import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fallbackServices } from '../data/fallbackServices';

const SERVICES_STORAGE_KEY = 'jatin_admin_services';
const PLACEHOLDER_IMAGE = 'https://picsum.photos/seed/service-placeholder/400/300.jpg';

const ServiceContext = createContext();

const normalizeService = (service) => {
  if (!service || typeof service !== 'object') {
    return {
      id: undefined,
      title: '',
      description: '',
      features: [],
      image: PLACEHOLDER_IMAGE,
    };
  }

  const rawFeatures = service.features;
  let features = [];

  if (Array.isArray(rawFeatures)) {
    features = rawFeatures.filter((feature) => typeof feature === 'string' && feature.trim().length > 0);
  } else if (typeof rawFeatures === 'string') {
    features = rawFeatures
      .split(/\r?\n|,/)
      .map((feature) => feature.trim())
      .filter((feature) => feature.length > 0);
  }

  return {
    ...service,
    features,
    image: service.image || PLACEHOLDER_IMAGE,
  };
};

const persistServices = (list) => {
  try {
    localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn('Could not persist services', e);
  }
};

export const useServices = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useServices must be used within a ServiceProvider');
  }
  return context;
};

export const ServiceProvider = ({ children }) => {
  const [services, setServices] = useState([]);
  const servicesRef = useRef(services);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    servicesRef.current = services;
  }, [services]);

  const showToastMessage = useCallback((message, type) => {
    setShowToast({ show: true, message, type });
    setTimeout(() => {
      setShowToast({ show: false, message: '', type: '' });
    }, 3000);
  }, []);

  const loadServices = useCallback(() => {
    setLoading(true);
    try {
      const raw = localStorage.getItem(SERVICES_STORAGE_KEY);
      let list = [];
      if (raw) {
        const parsed = JSON.parse(raw);
        list = Array.isArray(parsed) ? parsed : [];
      }
      if (list.length === 0) {
        list = fallbackServices;
      }
      setServices(list.map(normalizeService));
    } catch (e) {
      console.error('Failed to load services from storage:', e);
      setServices(fallbackServices.map(normalizeService));
      showToastMessage('Using default services.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToastMessage]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const addService = async (serviceData) => {
    const validFeatures = (serviceData.features || []).filter(Boolean);
    let created;
    setServices((prev) => {
      const id = Math.max(0, ...prev.map((s) => Number(s.id) || 0)) + 1;
      created = normalizeService({
        id,
        title: serviceData.title || '',
        description: serviceData.description || '',
        features: validFeatures,
        image: serviceData.image || PLACEHOLDER_IMAGE,
        order_field: prev.length + 1,
        is_active: true,
      });
      const next = [...prev, created];
      persistServices(next);
      return next;
    });
    showToastMessage('Service added successfully', 'success');
    return created;
  };

  const updateService = async (id, serviceData) => {
    const sid = Number(id);
    const prev = servicesRef.current;
    const idx = prev.findIndex((s) => Number(s.id) === sid || String(s.id) === String(id));
    if (idx === -1) {
      showToastMessage('Service not found', 'error');
      throw new Error('Service not found');
    }
    const existing = prev[idx];
    const validFeatures = Array.isArray(serviceData.features)
      ? serviceData.features.filter(Boolean)
      : existing.features;
    const updated = normalizeService({
      ...existing,
      ...serviceData,
      id: existing.id,
      features: validFeatures,
      image: serviceData.image || existing.image,
    });
    const next = prev.map((s) => (s.id === existing.id ? updated : s));
    setServices(next);
    persistServices(next);
    showToastMessage('Service updated successfully', 'success');
    return updated;
  };

  const deleteService = async (id) => {
    const sid = Number(id);
    setServices((prev) => {
      const next = prev.filter((s) => Number(s.id) !== sid && String(s.id) !== String(id));
      persistServices(next);
      return next;
    });
    showToastMessage('Service deleted successfully', 'success');
  };

  const getServiceById = async (id) => {
    const sid = Number(id);
    const found = servicesRef.current.find((s) => Number(s.id) === sid || String(s.id) === String(id));
    if (found) {
      return normalizeService(found);
    }
    showToastMessage('Service not found', 'error');
    throw new Error('Service not found');
  };

  const value = {
    services,
    loading,
    addService,
    updateService,
    deleteService,
    getServiceById,
    loadServices,
    showToast,
    showToastMessage,
  };

  return (
    <ServiceContext.Provider value={value}>
      {children}
      {showToast.show && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ${
            showToast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}
        >
          {showToast.message}
        </div>
      )}
    </ServiceContext.Provider>
  );
};
